// SearchProductCard - Simplified card for search results (click to navigate)
import { ChevronRight, Wrench, Heart } from 'lucide-react'
import { CheckingPriceBadge } from './CheckingPriceBadge'
import { CheckingStockBadge } from './CheckingStockBadge'
import { InStockBadge } from './InStockBadge'
import { BackorderBadge } from './BackorderBadge'
import { OutOfStockBadge } from './OutOfStockBadge'
import { NoNearbyBranchBadge } from './NoNearbyBranchBadge'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from '../engineer/favourites/useFavouriteToggle'
import { HOME_TAB_ENABLED } from '../../config/features'
import type { MasterProduct } from '../../types'

// Progressive per-card stock state. The Search page accumulates responses
// from N parallel per-supplier stock-check requests and maps each master's
// aggregated state into one of these. The card just renders the pill +
// optional dimmed styling — it does not own the stitching logic.
//
// - 'in_stock'     : at least one linked supplier confirmed stock
// - 'out_of_stock' : every linked supplier responded, none had stock
// - 'checking'     : at least one supplier still pending, and no positive
//                    response yet (a confirmed in_stock short-circuits
//                    even when other suppliers are still pending)
// - 'unknown'      : every supplier returned an error / no listing; we
//                    genuinely don't know. Card renders without any pill.
// - 'no_branch'    : the master is only carried by suppliers with no branch
//                    in range of the user's postcode. Backend-flagged (not
//                    derived from stock calls); the card renders the
//                    "No nearby branch" pill and pins to the bottom.
export type SearchCardStockState =
  | 'in_stock'
  // No stock at any nearby branch/depot, but a linked supplier can backorder
  // it (supplier checkbox on, product eligible, date available) — orderable.
  | 'backorder'
  | 'out_of_stock'
  | 'checking'
  | 'unknown'
  | 'no_branch'

// Per-master list of suppliers with an in-stock vote, sorted upstream by
// price ascending (nearest-branch tie-breaker). The first entry is the
// "primary" supplier the card highlights alongside its price + distance;
// the remainder render as "Also available at X, Y". `price` is the stored
// selling price of the raw that voted in-stock — may be null when the
// backend cache was populated before BM-001, in which case the card
// falls back to the master's absolute lowest price.
export interface InStockSupplierInfo {
  name: string
  distanceMi: number | null
  price: number | null
}

export interface SearchProductCardProps {
  product: MasterProduct
  // 1-indexed position in the visible result list. Forwarded to onClick so
  // the parent can record search-quality signals (which result position did
  // the user actually open?) without the card needing to know about analytics.
  position: number
  onClick: (product: MasterProduct, position: number) => void
  stockState?: SearchCardStockState
  inStockSuppliers?: InStockSupplierInfo[]
  // True while at least one supplier's live-price refresh is still in
  // flight for this master AND no usable price is available yet. Card
  // shows a pulsing "Checking price…" pill in place of the hyphen so
  // the user doesn't see a definitive "no price" during the fetch.
  isPriceRefreshing?: boolean
}

export function SearchProductCard({
  product,
  position,
  onClick,
  stockState = 'unknown',
  inStockSuppliers = [],
  isPriceRefreshing = false,
}: SearchProductCardProps) {
  // Card background + dim title only on CONFIRMED out-of-stock — never
  // while still checking, since we don't want to flash a grey card and
  // then reveal it as in-stock once the pill resolves.
  const showOosStyling = stockState === 'out_of_stock'

  // Prioritised mode shows its matches as linked product cards
  // (LinkedProductCard), so this grouped card always shows the aggregate.
  const badgeState: SearchCardStockState = stockState

  // "Also available at" lists the in-stock suppliers OTHER than the one in
  // the headline (the cheapest, index 0) — filtered by name rather than
  // assuming index 0 is always the headline.
  const headlineSupplierForAlternatives = inStockSuppliers[0]?.name
  const alternativeSuppliers = inStockSuppliers.filter(
    (supplier) => supplier.name !== headlineSupplierForAlternatives,
  )

  // Heart-icon state — DEV-46921. Search cards are master-product
  // results, so the toggle here targets the master (`product.id`).
  // The heart is a nested button that stops propagation so the whole-
  // card tap gesture stays clean.
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()
  const favTarget = { type: 'master' as const, id: product.id }
  const favourited = isFavourited(favTarget)

  return (
    <div
      className={`relative rounded-2xl border border-gray-200 ${
        showOosStyling ? 'bg-[#F8F9FA]' : 'bg-white'
      }`}
    >
      <button
        onClick={() => onClick(product, position)}
        className={`w-full rounded-2xl p-4 sm:p-5 text-left hover:border-gray-300 hover:shadow-sm active:bg-gray-50 transition-all`}
      >
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.displayName}
              className="w-full h-full object-contain"
            />
          ) : (
            <Wrench className="w-8 h-8 text-gray-300" />
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0 pr-8">{/* pr-8 leaves room for the heart overlay */}
          {/* Title */}
          <h3
            className={`text-base sm:text-lg font-bold leading-tight mb-1 ${
              showOosStyling ? 'text-gray-500' : 'text-gray-900'
            }`}
          >
            {product.displayName}
          </h3>

          {/* Stock pill always sits on its own line below the title for
              consistent layout across all screen widths. Pill type swaps
              in place as the progressive stock-check resolves. */}
          {badgeState !== 'unknown' && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {badgeState === 'in_stock' && <InStockBadge />}
              {badgeState === 'backorder' && <BackorderBadge variant="amber-plain" />}
              {badgeState === 'out_of_stock' && <OutOfStockBadge />}
              {badgeState === 'checking' && <CheckingStockBadge />}
              {badgeState === 'no_branch' && <NoNearbyBranchBadge />}
            </div>
          )}

          {/* Product Code */}
          {product.canonicalMpn && (
            <p className="text-gray-400 text-xs mb-2">Product code: {product.canonicalMpn}</p>
          )}


          {/* Price + primary supplier row.
           *
           * In-stock path (BM-001, Noreen): headline is the cheapest
           * in-stock supplier's price, its name and its branch distance
           * — the number, the name, and the distance are all for the
           * same supplier so nothing can drift.
           *
           * Non-in-stock path (BM-001 follow-up): OOS / checking /
           * unknown cards show product.lowestPrice + lowestPriceSupplierName
           * (backend-computed from the same row that produced lowest_price).
           * No branch distance — a distance is only meaningful next to a
           * confirmed in-stock branch, and rendering "0.9 mi away" beside
           * an OOS card would misrepresent availability. */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-base sm:text-lg">
            {(() => {
              // Presence of an in-stock supplier is the source of truth for
              // the label — if we have one, its name (and its branch
              // distance) is what the card must show, even when the stored
              // price for that supplier is missing (legacy cache entries
              // pre-BM-001 stored distance only). Only the NUMBER falls
              // back to product.lowestPrice; the supplier identity never
              // silently switches to a different (potentially OOS) supplier
              // just because the price cache lacked a number.
              const primary = inStockSuppliers[0]
              const hasInStockSupplier = primary != null
              const headlinePrice =
                primary?.price != null && primary.price > 0
                  ? primary.price
                  : product.lowestPrice
              const headlineSupplierName = hasInStockSupplier
                ? primary.name
                : product.lowestPriceSupplierName
              const headlineDistanceMi = hasInStockSupplier
                ? primary.distanceMi
                : null
              const hasKnownPrice =
                headlinePrice != null && headlinePrice > 0
              // Suppress the definitive "-" while the live-price refresh
              // is still in flight for this master — the pill below tells
              // the user we're checking. Once every relevant supplier has
              // responded, the hyphen becomes the correct final answer.
              if (!hasKnownPrice && isPriceRefreshing) {
                return <CheckingPriceBadge />
              }
              return (
                <>
                  <span className="text-brand-primary font-bold">
                    {hasKnownPrice ? (
                      <>from £{headlinePrice.toFixed(2)}</>
                    ) : (
                      <>-</>
                    )}
                  </span>
                  {headlineSupplierName && (
                    <span className="text-gray-600 text-xs sm:text-sm">
                      · <span className="font-semibold text-gray-800">{headlineSupplierName}</span>
                      {headlineDistanceMi != null && (
                        <> · {headlineDistanceMi.toFixed(1)} mi</>
                      )}
                    </span>
                  )}
                </>
              )
            })()}
          </div>

          {/* Additional in-stock suppliers (the headline one is excluded).
           * Only shown when at least one other supplier voted in_stock so
           * we don't render "Also available at" with an empty list. */}
          {alternativeSuppliers.length > 0 && (
            <p className="mt-1 text-gray-500 text-xs">
              Also available at{' '}
              <span className="font-semibold text-gray-700">
                {alternativeSuppliers.map((supplier) => supplier.name).join(', ')}
              </span>
            </p>
          )}
        </div>
      </div>
      </button>

      {/* Favourite heart. Sits above the card button (z-10) with
       * pointer-events isolated so tapping it never fires the card's
       * navigation click. Gated by the HOME_TAB_ENABLED build flag
       * so environments that have the Home tab hidden don't show a
       * dangling heart that leads to a favourite the engineer would
       * never be able to see. */}
      {HOME_TAB_ENABLED && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            toggleFavourite({ target: favTarget, productName: product.displayName })
          }}
          // The ::before extends the tap area to 44px without changing the look.
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-sm hover:bg-white transition-colors before:absolute before:-inset-2 before:content-['']"
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
        >
          <Heart
            className={`w-4 h-4 ${favourited ? 'text-brand-primary' : 'text-gray-400'}`}
            fill={favourited ? 'currentColor' : 'none'}
          />
        </button>
      )}

      {/* Chevron — right-aligned to the same axis as the heart button.
       * `pointer-events-none` so it never intercepts the card's click
       * (the whole card is the button target). */}
      <ChevronRight
        aria-hidden="true"
        className="pointer-events-none absolute bottom-4 right-4 w-5 h-5 text-gray-400"
      />
    </div>
  )
}
